import {
  AuditOutlined,
  DeleteOutlined,
  EditOutlined,
  MoreOutlined,
  RollbackOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { QueryClient } from "@tanstack/react-query";
import { Menu, Dropdown, Button, Tooltip, notification } from "antd";
import { journalsQueries } from "@/entities/journal";
import {
  CommentIcon,
  HistoryIcon,
  RecallSignIcon,
} from "@/shared/assets/svg/icons";
import {
  fallbackString,
  formatDate,
  formatMoscowTime,
} from "@/shared/utils";
import { Can } from "@shared/lib/casl";
import { JournalCryptographyWrapper, UserBubble } from "@shared/ui";
import { getSectionOneActions } from "../actions-map";
import { statusIcons } from "../constants";
import { mapSectionItemToFormPayload } from "./map-section-ittem-to-form-payload";
import "./get-table-columns.scss";

const COLUMN_TOOLTIPS = {
  workDates:
    "Дата начала и окончания работ на объекте капитального строительства с указанием вида работ",
  company:
    "Полное и (или) сокращенное наименование или фамилия, имя отчество (последнее - при наличии) лица, осуществляющего строительство",
  person:
    "Фамилия, инициалы, должность (при наличии) лица, входящего в список инженерно-технического персонала",
  signer:
    "Уполномоченный представитель лица, осуществляющего строительство",
};

const renderColumnTitle = (label: string, tooltip?: string) =>
  tooltip ? (
    <Tooltip title={tooltip}>
      <span className="journals-section-first-table__column-title">{label}</span>
    </Tooltip>
  ) : (
    label
  );

const renderDatetimeCell = (date?: string | null) => {
  if (!date) {
    return "-";
  }

  const formattedDate = formatDate(date);
  const formattedTime = formatMoscowTime(date);

  if (!formattedDate && !formattedTime) {
    return "-";
  }

  return (
    <div className="journals-section-first-table__datetime">
      {formattedDate ? (
        <span className="journals-section-first-table__datetime-date">
          {formattedDate}
        </span>
      ) : null}
      {formattedTime ? (
        <span className="journals-section-first-table__datetime-time">
          {formattedTime} МСК
        </span>
      ) : null}
    </div>
  );
};

const renderNumberCell = (record: any) => {
  const { number } = record;
  const { updateDate } = record.rawItem ?? {};

  return (
    <div className="journals-section-first-table__number">
      <span className="journals-section-first-table__number-value">
        {number ?? "-"}
      </span>
      {updateDate ? (
        <>
          <span className="journals-section-first-table__number-updated">
            изменено {formatDate(updateDate)}
          </span>
          {formatMoscowTime(updateDate) ? (
            <span className="journals-section-first-table__datetime-time">
              {formatMoscowTime(updateDate)} МСК
            </span>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

type SectionOneRowActionsProps = {
  record: any;
  mainJournalData: any;
  handleView?: (rawItem: any) => void;
  handleEdit: (rawItem: any) => void;
  handleUpdate: (payload: {
    sectionOneListId: string;
    itemNoVersionId: string;
  }) => void;
  deleteSectionEntry: (
    params: { params: { path: { itemNoVersionId: string } } },
    options?: { onSuccess?: () => void },
  ) => void;
  setModalState: (state: any) => void;
  setVersionId: (versionId: string) => void;
  setVersionModalItemId: (itemNoVersionId: string) => void;
};

const SectionOneRowActions = ({
  record,
  mainJournalData,
  handleView,
  handleEdit,
  handleUpdate,
  deleteSectionEntry,
  setModalState,
  setVersionId,
  setVersionModalItemId,
}: SectionOneRowActionsProps) => {
  const baseViewItem = (
    <Menu.Item key="view" onClick={() => handleView?.(record?.rawItem)}>
      <>
        <EyeOutlined style={{ marginRight: 5 }} />
        Открыть запись
      </>
    </Menu.Item>
  );

  const menu = (
    <Menu>
      {baseViewItem}
      {getSectionOneActions(record?.rawItem.status).includes("sendToSign") && (
        <Menu.Item
          key="send"
          onClick={() =>
            setModalState({
              visible: true,
              title: "Отправка записи на подписание",
              info: "Вы действительно хотите отправить запись на подписание?",
              confirmText: "Отправить",
              cancelText: "Отмена",
              inputPlaceholder: "Комментарий",
              isInputModal: false,
              inputValue: "",
              onConfirm: null,
              action: {
                type: "SEND",
                payload: {
                  sectionOneListId:
                    mainJournalData?.mainJournal?.section1?.sectionListId || "",
                  itemNoVersionId: record?.key,
                },
              },
            })
          }
        >
          <>
            <AuditOutlined style={{ marginRight: 5 }} />
            Отправить на подписание
          </>
        </Menu.Item>
      )}
      {getSectionOneActions(record?.rawItem.status).includes("rejectSign") && (
        <Menu.Item
          key="reject"
          onClick={() =>
            setModalState({
              visible: true,
              title: "Отозвать с подписания",
              info: "Вы действительно хотите отозвать с подписания?",
              confirmText: "Отозвать",
              cancelText: "Отмена",
              inputPlaceholder: "Комментарий",
              isInputModal: false,
              inputValue: "",
              onConfirm: null,
              action: {
                type: "RECALL",
                payload: {
                  sectionOneListId:
                    mainJournalData?.mainJournal?.section1?.sectionListId || "",
                  itemNoVersionId: record?.key,
                },
              },
            })
          }
        >
          <div className="journal-table-menu-item">
            <RecallSignIcon style={{ marginRight: 5 }} />
            <div>Отозвать с подписания</div>
          </div>
        </Menu.Item>
      )}
      {getSectionOneActions(record?.rawItem.status).includes("edit") && (
        <Menu.Item
          key="edit"
          onClick={() => {
            if (record?.rawItem?.versionId) {
              setVersionId(record?.rawItem.versionId);
            }
            handleEdit(record?.rawItem);
          }}
        >
          <>
            <EditOutlined style={{ marginRight: 5 }} />
            Редактировать
          </>
        </Menu.Item>
      )}
      {getSectionOneActions(record?.rawItem.status).includes("rollback") && (
        <Menu.Item
          key="return-to-work"
          onClick={() => {
            const values = mapSectionItemToFormPayload(
              record?.rawItem,
              mainJournalData,
            );
            setModalState({
              visible: true,
              title: "Вернуть запись в работу",
              info: "Вы действительно хотите вернуть запись в статус NEW?",
              confirmText: "Вернуть",
              cancelText: "Отмена",
              onConfirm: () =>
                handleUpdate({
                  sectionOneListId: values.section1ListId,
                  itemNoVersionId: values.itemNoVersionId,
                }),
            });
          }}
        >
          <>
            <RollbackOutlined style={{ marginRight: 5 }} />
            Вернуть в работу
          </>
        </Menu.Item>
      )}
      {getSectionOneActions(record?.rawItem.status).includes("delete") && (
        <Menu.Item
          key="delete"
          onClick={() =>
            setModalState({
              visible: true,
              title: "Удаление записи",
              info: "Вы действительно хотите удалить запись?",
              confirmText: "Удалить",
              cancelText: "Отмена",
              onConfirm: () =>
                deleteSectionEntry(
                  {
                    params: { path: { itemNoVersionId: record?.key } },
                  },
                  {
                    onSuccess: () => {
                      notification.success({
                        message: "Запись удалена",
                      });
                    },
                  },
                ),
            })
          }
        >
          <>
            <DeleteOutlined style={{ marginRight: 5 }} />
            Удалить
          </>
        </Menu.Item>
      )}
      {getSectionOneActions(
        record?.rawItem.status,
        !!record?.rawItem?.versionId,
      ).includes("versions") ? (
        <Menu.Item
          key="versions"
          onClick={() => {
            setVersionModalItemId(record?.rawItem.itemNoVersionId);
          }}
        >
          <div className="journal-table-menu-item">
            <HistoryIcon style={{ marginRight: 5 }} />
            Версии
          </div>
        </Menu.Item>
      ) : null}
    </Menu>
  );

  const showVersions = getSectionOneActions(
    record?.rawItem.status,
    !!record?.rawItem?.versionId,
  ).includes("versions");

  const viewMenu = (
    <Menu>
      {baseViewItem}
      {showVersions ? (
        <Menu.Item
          key="versions"
          onClick={() => {
            setVersionModalItemId(record?.rawItem.itemNoVersionId);
          }}
        >
          <div className="journal-table-menu-item">
            <HistoryIcon style={{ marginRight: 5 }} />
            Версии
          </div>
        </Menu.Item>
      ) : null}
    </Menu>
  );

  return (
    <>
      <Can I="edit" a="section_1">
        <Dropdown overlay={menu} trigger={["click"]} placement="bottomRight">
          <Button
            style={{ border: "none", boxShadow: "none" }}
            icon={<MoreOutlined />}
            onClick={(event) => event.stopPropagation()}
          />
        </Dropdown>
      </Can>
      <Can not I="edit" a="section_1">
        <Dropdown overlay={viewMenu} trigger={["click"]} placement="bottomRight">
          <Button
            style={{ border: "none", boxShadow: "none" }}
            icon={<MoreOutlined />}
            onClick={(event) => event.stopPropagation()}
          />
        </Dropdown>
      </Can>
    </>
  );
};

export const getTableColumns = ({
  mainJournalData,
  localUser,
  signingString,
  setupSigningString,
  withSignedString,
  debouncedSearch,
  queryClient,
  handleEdit,
  handleView,
  handleUpdate,
  deleteSectionEntry,
  setModalState,
  signingItemId,
  setVersionId,
  setVersionModalItemId,
  bulkSendMode = false,
}: {
  mainJournalData: any;
  localUser: any;
  signingString: any;
  setupSigningString: any;
  withSignedString: any;
  debouncedSearch: any;
  queryClient: QueryClient;
  handleEdit: any;
  handleView?: any;
  handleUpdate: any;
  deleteSectionEntry: any;
  setModalState: any;
  signingItemId: string | null;
  setVersionId: any;
  setVersionModalItemId: any;
  bulkSendMode?: boolean;
}) => [
  {
    title: "№",
    dataIndex: "number",
    key: "number",
    width: 88,
    render: (_: unknown, record: any) => renderNumberCell(record),
  },
  {
    title: renderColumnTitle(
      "Дата начала и окончания работ на объекте и вид работ",
      COLUMN_TOOLTIPS.workDates,
    ),
    dataIndex: "workPeriod",
    key: "workPeriod",
    width: 220,
    className: "journals-section-first-table__text-cell",
    render: (_: unknown, record: any) => {
      const { dateStart, dateEnd, workType } = record?.rawItem ?? {};
      const start = formatDate(dateStart);
      const end = formatDate(dateEnd);
      const period =
        start && end ? `${start} - ${end}` : fallbackString(start || end);

      return (
        <div className="journals-section-first-table__work-period">
          <span className="journals-section-first-table__work-period-dates">
            {period}
          </span>
          <span className="journals-section-first-table__work-period-type journals-section-first-table__cell-text">
            {fallbackString(workType)}
          </span>
        </div>
      );
    },
  },
  {
    title: renderColumnTitle(
      "Лицо, осуществляющее строительство",
      COLUMN_TOOLTIPS.company,
    ),
    dataIndex: "fullName",
    key: "fullName",
    width: 200,
    className: "journals-section-first-table__text-cell",
    render: (text: string) => (
      <span className="journals-section-first-table__cell-text">
        {fallbackString(text)}
      </span>
    ),
  },
  {
    title: renderColumnTitle(
      "ФИО лица из инженерно-технического персонала",
      COLUMN_TOOLTIPS.person,
    ),
    dataIndex: "person",
    key: "person",
    width: 180,
    className: "journals-section-first-table__text-cell",
    render: (_: unknown, record: any) => {
      const user = record?.rawItem?.userCompany?.user;

      return (
        <>
          <UserBubble userId={user?.userId}>
            <div className="journal-table-person-link">
              <div className="journal-table-person-part">{user?.surname}</div>
              <div className="journal-table-person-part">
                {user?.firstName?.at(0)}.
              </div>
              <div className="journal-table-person-part">
                {user?.middleName?.at(0)}.
              </div>
            </div>
          </UserBubble>
          {record?.role ? (
            <div className="journals-section-first-table__person-role journals-section-first-table__cell-text">
              {record.role}
            </div>
          ) : null}
        </>
      );
    },
  },
  {
    title: renderColumnTitle("Подписант", COLUMN_TOOLTIPS.signer),
    dataIndex: "representative",
    key: "representative",
    width: 280,
    render: (_: unknown, record: any) => {
      const isMyEntry =
        record?.rawItem?.signer?.userId === localUser?.user_id &&
        record?.rawItem?.status === "AWAITING_SIGNATURE";
      return (
        <div className="journal-table-signer-cell">
          <div className="journal-table-signer-column">
            <div className="flex-default journal-table-signer-row">
              <span className="journal-table-signer-row">
                <div>
                  <UserBubble userId={record?.rawItem?.signer?.userId}>
                    <Button type="link" style={{ paddingLeft: 0 }}>
                      {record?.rawItem?.signer?.surname}{" "}
                      {record?.rawItem?.signer?.firstName?.at(0)}.
                      {record?.rawItem?.signer?.middleName?.at(0)}.
                    </Button>
                  </UserBubble>

                  <br />
                  <span className="journals-section-first-table__cell-text">
                    {record?.representativeRole}
                  </span>
                  {record?.status === "SIGNED" &&
                  record?.rawItem?.signer?.mchdId ? (
                    <span className="journals-section-first-table__cell-text">
                      Доверенность № {record.rawItem.signer.mchdId}
                    </span>
                  ) : null}
                </div>
                <div className="journal-table-signer-meta">
                  <div className="journal-table-status-icon-wrap">
                    {statusIcons[record?.status as keyof typeof statusIcons]}
                  </div>
                  {(() => {
                    const comments = record?.rawItem?.comments || [];
                    const validComments = comments.filter(
                      (comment: any) => comment?.comment && comment.comment.trim(),
                    );
                    return validComments.length > 0 ? (
                      <div className="journal-table-comment-wrap">
                        <Tooltip
                          title={validComments
                            .map((comment: any) => comment.comment)
                            .join("\n")}
                        >
                          <span className="journal-table-comment-trigger">
                            <div className="section-one-comment-chip">
                              <div className="section-one-comment-chip__text">
                                Комментарий
                              </div>
                              <div className="section-one-comment-chip__icon">
                                <div className="section-one-comment-chip__icon-box">
                                  <CommentIcon />
                                </div>
                              </div>
                            </div>
                          </span>
                        </Tooltip>
                      </div>
                    ) : null;
                  })()}
                </div>
              </span>
            </div>
            {isMyEntry && !bulkSendMode && (
              <div className="journal-table-sign-actions">
                <Can I="sign" a="section_1">
                  <JournalCryptographyWrapper
                    currentUser={localUser}
                    signContent={signingString}
                    onSuccess={(xml, _proxyId, mchdId) =>
                      withSignedString(
                        {
                          sectionOneListId:
                            mainJournalData?.mainJournal?.section1
                              ?.sectionListId || "",
                          itemNoVersionId: record?.rawItem.itemNoVersionId,
                          signerEmployeeId: localUser?.user_id || "",
                          onSuccess: () =>
                            queryClient.invalidateQueries({
                              queryKey: journalsQueries.getSection1({
                                sectionOneId:
                                  mainJournalData?.mainJournal?.section1
                                    ?.sectionId || "",
                                search: debouncedSearch,
                              }).queryKey,
                            }),
                        },
                        xml,
                        mchdId,
                      )
                    }
                  >
                    {({ onOpen }) => (
                      <Button
                        type="primary"
                        loading={signingItemId === record?.rawItem?.itemNoVersionId}
                        style={{ marginRight: 10 }}
                        onClick={() =>
                          setupSigningString(
                            {
                              sectionOneListId:
                                mainJournalData?.mainJournal?.section1
                                  ?.sectionListId || "",
                              itemNoVersionId: record?.rawItem.itemNoVersionId,
                              signerEmployeeId: localUser?.user_id || "",
                            },
                            onOpen,
                          )
                        }
                      >
                        Подписать
                      </Button>
                    )}
                  </JournalCryptographyWrapper>
                </Can>
                <Can I="sign" a="section_1">
                  <Button
                    type="dashed"
                    onClick={() =>
                      setModalState({
                        visible: true,
                        title: "Отклонить подписание",
                        info: "Вы действительно хотите отклонить подписание?",
                        confirmText: "Отклонить",
                        cancelText: "Отмена",
                        inputPlaceholder: "Комментарий",
                        isInputModal: true,
                        inputValue: "",
                        onConfirm: null,
                        action: {
                          type: "CANCEL_SIGN",
                          payload: {
                            sectionOneListId:
                              mainJournalData?.mainJournal?.section1
                                ?.sectionListId || "",
                            itemNoVersionId: record?.key,
                            comment: "",
                            signerEmployeeId: localUser?.user_id || "",
                          },
                        },
                      })
                    }
                  >
                    Отклонить
                  </Button>
                </Can>
              </div>
            )}
          </div>
        </div>
      );
    },
  },
  {
    title: "Дата создания",
    dataIndex: "createDate",
    key: "createDate",
    width: 120,
    className: "journals-section-first-table__datetime-cell",
    render: (_: unknown, record: any) => (
      <div className="journal-table-datetime-actions-cell">
        {renderDatetimeCell(record?.rawItem?.createDate)}
        {bulkSendMode ? null : (
          <SectionOneRowActions
            record={record}
            mainJournalData={mainJournalData}
            handleView={handleView}
            handleEdit={handleEdit}
            handleUpdate={handleUpdate}
            deleteSectionEntry={deleteSectionEntry}
            setModalState={setModalState}
            setVersionId={setVersionId}
            setVersionModalItemId={setVersionModalItemId}
          />
        )}
      </div>
    ),
  },
];
